import React from 'react';
import './Resume.css';
import PrtButton from './PrtButton';

export default function Resume() {
  return (
    <div className="resume-page">
      <PrtButton label="Print Page" />

      {/* Header */}
      <header className="resume-header">
        <h1>Joseph William Phillip Bodden Jr.</h1>
        <p className="contact">
          Poway, CA • <a href="mailto:joeitguy@gmail.com">joeitguy@gmail.com</a> • 619-438-3236 • 
          <a href="https://linkedin.com/in/joeitguy"> linkedin.com/in/joeitguy</a>
        </p>
        <p className="tagline">
          Data & Technology Consultant | Analytics Architecture | Automation & Integration Solutions
        </p>
      </header>

      {/* Professional Summary */}
      <section className="summary">
        <h2>Professional Summary</h2>
        <p>
          Versatile data professional with a proven record of designing, optimizing, and automating
          multi-database pipelines across SQL Server, Postgres, and Python environments. Combines
          analytical precision with creative problem-solving to deliver scalable reporting, data integrity,
          and operational efficiency. Experienced in full-stack development, ETL architecture, and
          cross-team collaboration — bridging technical depth with business insight. A U.S. Navy veteran
          who brings the same discipline, consistency, and team-first mindset to every project. Passionate
          about transforming raw data into actionable intelligence that drives smarter decisions and
          strengthens organizational performance.
        </p>
      </section>

{/* Professional Experience */}
<section className="experience">
  <h2>Professional Experience</h2>

  <div className="job">
    <h3>Integration Support Analyst</h3>
    <p>Vistage Worldwide, Inc. <span>Sep 2018 – Mar 2025</span></p>
    <p>
      At Vistage, I served as the bridge between data and operations — making sure our systems
      spoke the same language. I supported integration across multiple enterprise platforms using
      T-SQL, Jira, and Splunk to identify and resolve data sync issues before they impacted
      production. I built automated reporting pipelines and SSRS dashboards that gave teams
      real-time visibility into key metrics, and debugged Python scripts to streamline data validation
      and report delivery. Collaboration was central to every project — I worked closely with
      developers, QA, and business stakeholders to translate technical findings into clear, actionable
      solutions. That blend of technical precision and open communication became my signature
      approach to integration support.
    </p>

    <ul>
      <li>Automated cross-system reporting pipelines using T‑SQL, SSRS, and Python, improving data consistency and reducing manual reconciliation across teams.</li>
      <li>Diagnosed integration failures using Splunk log analysis, enabling faster root‑cause identification and preventing downstream data corruption.</li>
      <li>Developed SQL-driven dashboards that provided real‑time visibility into sync status, helping teams catch issues before they reached production.</li>
      <li>Streamlined data validation workflows by enhancing Python scripts and SQL checks, reducing repetitive QA cycles and improving deployment reliability.</li>
      <li>Collaborated with developers, QA, and business teams to translate technical findings into actionable fixes, improving communication flow across departments.</li>
      <li>Documented integration dependencies and workflows, strengthening long‑term maintainability and reducing onboarding time for new team members.</li>
    </ul>
  </div>

  <div className="job">
    <h3>Data Reporting Analyst</h3>
    <p>Pala Casino Spa & Resort <span>Mar 2017 – Sep 2018</span></p>
    <p>
      I designed and maintained SQL-based reporting systems that supported daily casino
      operations and executive decision-making. Working closely with finance and marketing
      teams, I translated data into insights that improved accuracy and clarity across departments.
      My work combined technical precision with a strong sense of partnership — I enjoyed helping
      non-technical users understand how data could drive better outcomes.
    </p>

    <ul>
      <li>Built SQL-based reporting solutions using SSRS and T‑SQL, supporting daily casino operations and executive decision‑making.</li>
      <li>Analyzed financial and marketing datasets to surface trends, improving campaign accuracy and operational clarity across departments.</li>
      <li>Optimized stored procedures and indexing strategies, reducing report execution time and improving system responsiveness.</li>
      <li>Partnered with non‑technical stakeholders to refine KPIs and metric definitions, improving data governance and reporting consistency.</li>
      <li>Created automated validation checks that reduced data discrepancies and improved trust in reporting outputs.</li>
    </ul>
  </div>

  <div className="job">
    <h3>Senior Systems Analyst</h3>
    <p>Jackson Rancheria Casino <span>Nov 2012 – Sep 2017</span></p>
    <p>
      I managed and supported core gaming and reporting systems in a 24/7 environment,
      ensuring data integrity and system uptime. Using SQL and SSRS, I diagnosed and resolved
      data-related issues while documenting workflows to streamline future maintenance.
      Leading peer training sessions was a highlight — it let me combine technical mentorship with
      team development, building trust and shared expertise across the department.
    </p>

    <ul>
      <li>Supported core gaming and reporting systems in a 24/7 environment, ensuring high availability and reliable data flow across operations.</li>
      <li>Resolved data discrepancies using SQL diagnostics and SSRS reporting, improving accuracy for financial, gaming, and marketing teams.</li>
      <li>Documented system workflows and troubleshooting procedures, reducing repeat incidents and improving team efficiency.</li>
      <li>Led peer training sessions focused on SQL fundamentals, reporting tools, and system operations, strengthening team capability and collaboration.</li>
      <li>Coordinated system upgrades and vendor integrations, ensuring smooth transitions and minimal downtime during critical operational windows.</li>
    </ul>
  </div>
</section>


      {/* Core Competencies */}
      <section className="competencies">
        <h2>Core Competencies</h2>
        <ul>
          <li>SQL Programming & Query Optimization</li>
          <li>ETL Pipeline Design & Data Migration</li>
          <li>Python Development & Automation (Scripting, APIs, Data Wrangling)</li>
          <li>Data Modeling & Governance Compliance</li>
          <li>Reporting & Dashboard Development (Power BI, Tableau)</li>
          <li>Cross-System Integration & Troubleshooting</li>
          <li>Agile Collaboration & Technical Documentation</li>
        </ul>
      </section>

      {/* Military Service */}
      <section className="military">
        <h2>Military Service</h2>
        <p>
          U.S. Navy — Hospital Corpsman (Veteran)<br />
          <em>DD-214 upon request</em>
        </p>
      </section>

      {/* Education */}
      <section className="education">
        <h2>Education</h2>
        <p>
          <strong>American InterContinental University</strong><br />
          AS, Business Administration & Management <span>2009 – 2011</span>
        </p>
      </section>

      {/* Certifications */}
      <section className="certifications">
        <h2>Certifications</h2>
        <ul>
          <li>PCEP Certified Entry-Level Python Programmer</li>
          <li>
            Salesforce Trailhead Badge: Data Exploration with Data 360 SQL Queries — 
            <a href="https://www.salesforce.com/trailblazer/jbodden1" target="_blank" rel="noopener noreferrer">
              Trailblazer Profile
            </a>
          </li>
          <li>Ongoing learning in Cloud Data Engineering (AWS, Azure, GCP)</li>
        </ul>
      </section>

      {/* Technical Toolkit */}
      <section className="toolkit">
        <h2>Technical Toolkit</h2>
        <p>
          <strong>Languages:</strong> SQL, Python, HTML/CSS, JavaScript<br />
          <strong>Databases:</strong> Postgres, SQL Server, SQLite<br />
          <strong>Tools:</strong> Docker, Power BI, Tableau, Git, VS Code, Jupyter Lab
        </p>
      </section>

      {/* Professional Focus */}
      <section className="focus">
        <h2>Professional Focus</h2>
        <p>
          Seeking opportunities within the Data & Technology ecosystem — from engineering and
          analytics to integration and automation. Brings a hands-on approach to data reliability,
          scalability, and insight generation. Ready to contribute to projects that connect technical
          precision with business impact.
        </p>
      </section>
    </div>
  );
}
